import type { Locale } from "./contracts/locales";
import { AttributeKey } from "./contracts/attributes";

export const userRoles = ["ADMIN", "USER"] as const;
export type UserRole = (typeof userRoles)[number];

export const sourceTypes = [
  "PARARIUS",
  "FUNDA",
  "KAMERNET",
  "HOUSING_ANYWHERE",
  "HAUSING",
  "APPARTEMENT_MAASTRICHT",
  "VESTEDA",
  "XIOR",
  "DIRECT_WONEN",
  "RENTAL_ROTTERDAM",
  "RIVA_RENTALS",
  "I_AM_EXPAT",
  "ROTTERDAM_APARTMENTS",
  "WOONNET_HAAGLANDED",
  "WOONMATCH_WATERLAND",
  "SVNK",
  "VB_T",
  "ZUID_WEST_WONEN",
  "WOONTIJ",
  "WONEN_LIMBURG_ACCENT",
  "WOONKEUS_STEDENDRIEHOEK",
  "THUIS_IN_LIMBURG",
  "THUISKOMPAS",
  "WONING_ZICHT",
  "THUISPOORT",
  "PLAZA",
  "THUIS_IN_ACHTERHOEK",
  "ROOMSPOT",
  "ROOMATCH",
  "OOST_WEST_WONEN",
  "OFW",
  "MERCATUS",
  "KLIK_VOOR_WONEN",
  "KLIK_VOOR_KAMERS",
  "HUREN_IN_RIJNLAND",
  "FRIESLAND_HUURT",
  "DE_WONINGZOEKER",
  "ANTARES",
  "ACANTUS",
  "WOONMATCH_WEST_FRIESLAND",
  "WOONMATCHKOPNH",
  "WOONSTAD_ROTTERDAM",
  "DE_ALLIANTIE",
  "VESTIDE_STUDENT",
  "STUDENTEN_WONING_WEB",
  "STADSWONEN_ROTTERDAM",
  "SSHXL",
  "SCHEP_VASTGOEDMANAGERS",
  "RENTUMO",
  "ROOFZ",
  "MAGIS_RENT",
  "IK_WIL_HUREN",
  "CENTRADA",
  "WONEN_BIJ_BOUWINVEST",
] as const;
export type SourceType = (typeof sourceTypes)[number];

export const ownerTypes = ["PRIVATE", "AGENCY"] as const;
export type OwnerType = (typeof ownerTypes)[number];

export type ListingAttributes = Record<
  AttributeKey,
  number | boolean | string | Record<string, string> | string[]
>;

export const furnishingTypes = ["SHELL", "FURNISHED", "UPHOLSTERED"] as const;
export type FurnishingType = (typeof furnishingTypes)[number];

export const rentPriceBenchmarkLevels = [
  "CITY_TYPE_ROOMS_SIZE_FURNISHING",
  "CITY_TYPE_ROOMS_SIZE",
  "CITY_TYPE_SIZE",
  "PROVINCE_TYPE_ROOMS_SIZE",
] as const;

export const rentPriceBenchmarkLocationTypes = ["CITY", "PROVINCE"] as const;

export const rentPriceRoomsBuckets = [
  "UNKNOWN",
  "1",
  "2",
  "3",
  "4_PLUS",
] as const;

export const rentPriceSizeBuckets = [
  "UNDER_35",
  "35_49",
  "50_69",
  "70_89",
  "90_119",
  "120_PLUS",
] as const;

export const listingTypes = ["RENT", "SALE"] as const;
export type ListingType = (typeof listingTypes)[number];

export const issueReportTypes = ["LISTING", "OWNER"] as const;
export type IssueReportType = (typeof issueReportTypes)[number];

export type IssueReportMetadata = {
  language: string;
};

export const locationTypes = [
  "PROVINCE",
  "CITY",
  "BOROUGH",
  "NEIGHBORHOOD",
  "STREET",
  "COUNTRY",
] as const;
export type LocationType = (typeof locationTypes)[number];

export const locationNameLocaleOptions = [
  "en",
  "es",
  "de",
  "fr",
  "pl",
  "nl",
  "et",
  "is",
  "sv",
  "fi",
  "mt",
] as const;

export type LocationNameLocale = (typeof locationNameLocaleOptions)[number];

export type LocationLocalizedNames = Partial<
  Record<LocationNameLocale, string>
>;

export type LocationFact = {
  label: string;
  value: string;
};

export const locationSources = ["WIKIPEDIA", "OFFICIAL", "OTHER"] as const;
export type LocationSource = (typeof locationSources)[number];

export const contentFeedbackTypes = ["COMPARE-SUMMARY", "ARTICLE"] as const;
export type ContentFeedbackType = (typeof contentFeedbackTypes)[number];

export const pageMetaTypeOptions = [
  "HOME",
  "SEARCH",
  "MAP",
  "COMPARE",
  "FAQ",
  "CONTACT",
  "PUBLISH_HOME",
  "API_HOME",
  "API_RENTAL_LISTINGS",
  "API_RENTAL_MARKET",
  "API_HOUSING_RESOURCES",
  "API_PRICING",
  "RENT_HOME",
  "RENT_DISCOVER",
  "RENT_LOCATION_HOME",
  "RENT_LOCATION_EXPLORE_HOME",
  "RENT_LOCATION_EXPLORE_DETAIL",
  "RENT_LOCATION_PROPERTY_TYPE_HOME",
  "RENT_LOCATION_PROPERTY_TYPE_DETAIL",
  "OWNER_AGENCY_LIST",
  "RESOURCE_HOME",
  "RESOURCE_SEARCH",
  "RESOURCE_CATEGORY_LIST",
  "RESOURCE_CATEGORY_DETAIL",
  "RESOURCE_GLOSSARY_HOME",
  "RESOURCE_GLOSSARY_DETAIL",
  "RESOURCE_GLOSSARY_SEARCH",
  "RESOURCE_GLOSSARY_TYPE_LIST",
  "RESOURCE_GLOSSARY_TYPE_DETAIL",
  "RESOURCE_HANDBOOK_HOME",
  "RESOURCE_HANDBOOK_BEST",
  "RESOURCE_LOCATION_PROVINCE_LIST",
  "RESOURCE_LOCATION_PROVINCE_DETAIL_CITY_LIST",
  "RESOURCE_LOCATION_CITY_LIST",
  "RESOURCE_LOCATION_CITY_DETAIL",
  "RESOURCE_LOCATION_CITY_DETAIL_BOROUGH_LIST",
  "RESOURCE_LOCATION_BOROUGH_LIST",
  "RESOURCE_LOCATION_BOROUGH_DETAIL_NEIGHBORHOOD_LIST",
  "RESOURCE_LOCATION_NEIGHBORHOOD_LIST",
  "RESOURCE_MARKET_HOME",
  "RESOURCE_MARKET_CITY_LIST",
  "RESOURCE_MARKET_CITY_DETAIL",
  "RESOURCE_MARKET_RANKING_LIST",
  "RESOURCE_MARKET_METRIC_LIST",
  "RESOURCE_MARKET_METRIC_DETAIL",
  "RESOURCE_MARKET_METRIC_LOCATION_DETAIL",
  "RESOURCE_MARKET_REPORT_LIST",
  "RESOURCE_MARKET_REPORT_DETAIL",
  "RESOURCE_MARKET_REPORT_LOCATION_DETAIL",
  "RESOURCE_MARKET_MONTHLY_REPORT_LIST",
  "RESOURCE_PLATFORM_HOME",
  "RESOURCE_PLATFORM_DETAIL",
  "RESOURCE_PLATFORM_SEARCH",
  "RESOURCE_PLATFORM_COMPARISON_LIST",
  "RESOURCE_PLATFORM_PROVINCE_LIST",
  "RESOURCE_PLATFORM_PROVINCE_DETAIL",
  "RESOURCE_PLATFORM_CITY_LIST",
  "RESOURCE_PLATFORM_CITY_DETAIL",
  "RESOURCE_INTERNET_HOME",
  "RESOURCE_INTERNET_SEARCH",
  "RESOURCE_INTERNET_COMPARE",
  "RESOURCE_INTERNET_FINDER",
  "RESOURCE_INTERNET_PROVIDER_DETAIL",
  "RESOURCE_INTERNET_NETWORK_LIST",
  "RESOURCE_INTERNET_NETWORK_DETAIL",
  "RESOURCE_ENERGY_LABEL_FINDER",
  "RESOURCE_INSURANCE_HOME",
  "RESOURCE_INSURANCE_SEARCH",
  "RESOURCE_INSURANCE_PROVIDER_DETAIL",
  "RESOURCE_AIR_QUALITY_HOME",
  "RESOURCE_AIR_QUALITY_MAP",
  "RESOURCE_AIR_QUALITY_COMPONENT_LIST",
  "RESOURCE_AIR_QUALITY_COMPONENT_DETAIL",
  "RESOURCE_AIR_QUALITY_STATION_LIST",
  "RESOURCE_AIR_QUALITY_STATION_DETAIL",
  "RESOURCE_AIR_QUALITY_CITY_LIST",
  "PRO_HOME",
  "PRO_ORGANIZER",
  "PRO_SEARCH_ALERT",
  "PRO_SMART_MESSAGE",
] as const;
export type PageMetaType = (typeof pageMetaTypeOptions)[number];

export const pageMetaTypePathPatterns = {
  HOME: "/",
  SEARCH: "/search",
  MAP: "/map",
  COMPARE: "/compare",
  FAQ: "/faq",
  CONTACT: "/contact",
  PUBLISH_HOME: "/publish",
  API_HOME: "/developers",
  API_RENTAL_LISTINGS: "/developers/rental-listings-api",
  API_RENTAL_MARKET: "/developers/rental-market-api",
  API_HOUSING_RESOURCES: "/developers/housing-resources-api",
  API_PRICING: "/developers/pricing",
  RENT_HOME: "/rent",
  RENT_DISCOVER: "/rent/discover",
  RENT_LOCATION_HOME: "/rent/:location-slug",
  RENT_LOCATION_EXPLORE_HOME: "/rent/:location-slug/explore",
  RENT_LOCATION_EXPLORE_DETAIL: "/rent/:location-slug/explore/:explore-type",
  RENT_LOCATION_PROPERTY_TYPE_HOME: "/rent/:location-slug/property-type",
  RENT_LOCATION_PROPERTY_TYPE_DETAIL:
    "/rent/:location-slug/property-type/:property-type",
  OWNER_AGENCY_LIST: "/owner/agency/list",
  RESOURCE_HOME: "/resource",
  RESOURCE_SEARCH: "/resource/search",
  RESOURCE_CATEGORY_LIST: "/resource/category",
  RESOURCE_CATEGORY_DETAIL: "/resource/category/:category-slug",
  RESOURCE_GLOSSARY_HOME: "/resource/glossary",
  RESOURCE_GLOSSARY_DETAIL: "/resource/glossary/term/:term-slug",
  RESOURCE_GLOSSARY_SEARCH: "/resource/glossary/search",
  RESOURCE_GLOSSARY_TYPE_LIST: "/resource/glossary/type",
  RESOURCE_GLOSSARY_TYPE_DETAIL: "/resource/glossary/type/:type-slug",
  RESOURCE_HANDBOOK_HOME: "/resource/handbook",
  RESOURCE_HANDBOOK_BEST: "/resource/handbook/best",
  RESOURCE_LOCATION_PROVINCE_LIST: "/resource/location/province",
  RESOURCE_LOCATION_PROVINCE_DETAIL_CITY_LIST:
    "/resource/location/province/:province-slug/cities",
  RESOURCE_LOCATION_CITY_LIST: "/resource/location/city",
  RESOURCE_LOCATION_CITY_DETAIL: "/resource/location/city/:city-slug",
  RESOURCE_LOCATION_CITY_DETAIL_BOROUGH_LIST:
    "/resource/location/city/:city-slug/boroughs",
  RESOURCE_LOCATION_BOROUGH_LIST: "/resource/location/borough",
  RESOURCE_LOCATION_BOROUGH_DETAIL_NEIGHBORHOOD_LIST:
    "/resource/location/borough/:borough-slug/neighborhoods",
  RESOURCE_LOCATION_NEIGHBORHOOD_LIST: "/resource/location/neighborhood",
  RESOURCE_MARKET_HOME: "/resource/market",
  RESOURCE_MARKET_CITY_LIST: "/resource/market/city",
  RESOURCE_MARKET_CITY_DETAIL: "/resource/market/city/:city-slug",
  RESOURCE_MARKET_RANKING_LIST: "/resource/market/rankings",
  RESOURCE_MARKET_METRIC_LIST: "/resource/market/metrics",
  RESOURCE_MARKET_METRIC_DETAIL: "/resource/market/metrics/:metric-slug",
  RESOURCE_MARKET_METRIC_LOCATION_DETAIL:
    "/resource/market/metrics/:metric-slug/:location-slug",
  RESOURCE_MARKET_REPORT_LIST: "/resource/market/reports",
  RESOURCE_MARKET_REPORT_DETAIL: "/resource/market/reports/:report-slug",
  RESOURCE_MARKET_REPORT_LOCATION_DETAIL:
    "/resource/market/reports/:report-slug/:location-slug",
  RESOURCE_MARKET_MONTHLY_REPORT_LIST: "/resource/market/monthly-report",
  RESOURCE_PLATFORM_HOME: "/resource/platform",
  RESOURCE_PLATFORM_DETAIL: "/resource/platform/:platform-slug",
  RESOURCE_PLATFORM_SEARCH: "/resource/platform/search",
  RESOURCE_PLATFORM_COMPARISON_LIST: "/resource/platform/comparison",
  RESOURCE_PLATFORM_PROVINCE_LIST: "/resource/platform/province",
  RESOURCE_PLATFORM_PROVINCE_DETAIL:
    "/resource/platform/province/:province-slug",
  RESOURCE_PLATFORM_CITY_LIST: "/resource/platform/city",
  RESOURCE_PLATFORM_CITY_DETAIL: "/resource/platform/city/:city-slug",
  RESOURCE_INTERNET_HOME: "/resource/internet",
  RESOURCE_INTERNET_SEARCH: "/resource/internet/search",
  RESOURCE_INTERNET_COMPARE: "/resource/internet/compare",
  RESOURCE_INTERNET_FINDER: "/resource/internet/finder",
  RESOURCE_INTERNET_PROVIDER_DETAIL: "/resource/internet/:provider-slug",
  RESOURCE_INTERNET_NETWORK_LIST: "/resource/internet/network",
  RESOURCE_INTERNET_NETWORK_DETAIL: "/resource/internet/network/:network-slug",
  RESOURCE_ENERGY_LABEL_FINDER: "/resource/energy-label",
  RESOURCE_INSURANCE_HOME: "/resource/insurance",
  RESOURCE_INSURANCE_SEARCH: "/resource/insurance/search",
  RESOURCE_INSURANCE_PROVIDER_DETAIL: "/resource/insurance/:provider-slug",
  RESOURCE_AIR_QUALITY_HOME: "/resource/air-quality",
  RESOURCE_AIR_QUALITY_MAP: "/resource/air-quality/map",
  RESOURCE_AIR_QUALITY_COMPONENT_LIST: "/resource/air-quality/component",
  RESOURCE_AIR_QUALITY_COMPONENT_DETAIL:
    "/resource/air-quality/component/:component-slug",
  RESOURCE_AIR_QUALITY_STATION_LIST: "/resource/air-quality/station",
  RESOURCE_AIR_QUALITY_STATION_DETAIL:
    "/resource/air-quality/station/:station-slug",
  RESOURCE_AIR_QUALITY_CITY_LIST: "/resource/air-quality/city",
  PRO_HOME: "/pro",
  PRO_ORGANIZER: "/pro/organizer",
  PRO_SEARCH_ALERT: "/pro/search-alert",
  PRO_SMART_MESSAGE: "/pro/smart-message",
} as const satisfies Record<PageMetaType, string>;

export const pointOfInteretTypes = [
  "UNIVERSITY",
  "TRAIN",
  "BUS",
  "TRAM",
  "SUBWAY",
  "AIRPORT",
  "RESTAURANT",
  "CAFE",
  "FAST_FOOD",
  "BAR",
  "HOSPITAL",
  "CINEMA",
  "BEACH",
  "SUPERMARKET",
  "MARKETPLACE",
  "LIBRARY",
  "POLICE",
  "FIRE_STATION",
  "AMBULANCE_STATION",
] as const;
export type PoiType = (typeof pointOfInteretTypes)[number];

export const poiCategories = [
  "EDUCATION",
  "TRANSPORTATION",
  "SHOPPING",
  "EATING",
  "RECREATION",
  "HEALTH_SAFETY",
] as const;
export type PoiCategory = (typeof poiCategories)[number];

export const poiCategoryTypes: Record<PoiCategory, PoiType[]> = {
  EDUCATION: ["UNIVERSITY", "LIBRARY"],
  TRANSPORTATION: ["TRAIN", "BUS", "TRAM", "SUBWAY", "AIRPORT"],
  SHOPPING: ["SUPERMARKET", "MARKETPLACE"],
  EATING: ["RESTAURANT", "CAFE", "FAST_FOOD", "BAR"],
  RECREATION: ["CINEMA", "BEACH"],
  HEALTH_SAFETY: ["HOSPITAL", "POLICE", "FIRE_STATION", "AMBULANCE_STATION"],
};

export type LocationExtraData = {
  code?: string;
  averageTotalElectricityDelivery?: number;
  numApartments?: number;
  terracedHouseOrTownhouse?: number;
  cornerHouse?: number;
  semiDetachedHouse?: number;
  detachedHouse?: number;
  rentalHousing?: number;
  ownerOccupiedHousing?: number;
  distanceToGP?: number;
  distanceToSupermarket?: number;
  distanceToDaycare?: number;
  distanceToSchool?: number;
  schoolsWithin3km?: number;
  totalArea?: number;
  landArea?: number;
  waterArea?: number;
  population?: number;
  aged0to15?: number;
  aged15to25?: number;
  aged25to45?: number;
  aged45to65?: number;
  aged65older?: number;
  totalHouseholds?: number;
  singlePersonHouseholds?: number;
  householdsWithoutChildren?: number;
  householdsWithChildren?: number;
  averageHouseholdSize?: number;
  populationDensity?: number;
  averagePropertyValue?: number;
  percentageSingleFamilyHomes?: number;
  percentageMultiFamilyHomes?: number;
  constructedBefore2000?: number;
  constructedAfter2000?: number;
};

export const isochroneModes = ["WALK", "BICYCLE", "DRIVE", "TRANSIT"] as const;
export type IsochroneMode = (typeof isochroneModes)[number];

export const isochroneTypes = ["TIME", "DISTANCE"] as const;
export type IsochroneType = (typeof isochroneTypes)[number];

export const newsArticleSourceTypes = [
  "DUTCH_NEWS",
  "I_AM_EXPAT",
  "NL_TIMES",
] as const;
export type NewsArticleSourceType = (typeof newsArticleSourceTypes)[number];

export const rentalTermTypes = [
  "CONTRACT_TYPES",
  "RENTAL_COSTS",
  "TENANT_RIGHTS",
  "LANDLORD_OBLIGATIONS",
  "SUBSIDIES_AND_ALLOWANCES",
  "PROPERTY_FEATURES",
  "APPLICATION_PROCESS",
  "LEGAL_TERMS",
  "DUTCH_HOUSING_SYSTEM",
  "EXPAT_CONSIDERATIONS",
  "OTHER",
] as const;
export type RentalTermType = (typeof rentalTermTypes)[number];

export type OwnerReviewData = {
  lastUpdated: Date;
  aggregate: {
    value: number;
    count: number;
    confidence: "low" | "medium" | "high";
  } | null;
  sources: Array<{
    name: string;
    url: string;
    ratingValue: number;
    bestRating: number;
    ratingCount: number;
    lastChecked: Date;
  }>;
};

export type OwnerSocialLinks = {
  facebook?: string;
  twitter?: string;
  instagram?: string;
  linkedin?: string;
  youtube?: string;
  tiktok?: string;
};

export type OwnerMetadata = {
  lastEmployeeCheck?: Date;
  lastReviewsCheck?: Date;
};

export const airQualityStationTypes = [
  "REGIONAL",
  "MUNICIPAL",
  "TRAFFIC",
  "INDUSTRIAL",
  "OTHER",
] as const;

export const airQualityComponent = [
  "LKI",
  "NO_2",
  "PM_10",
  "PM_2_5",
  "O_3",
] as const;

export const airQualityUnit = ["INDEX", "UG_M3"] as const;

export type AirQualityComponent = (typeof airQualityComponent)[number];
export type AirQualityUnit = (typeof airQualityUnit)[number];
export type AirQualityStationType = (typeof airQualityStationTypes)[number];

export const resourceSearchFamilies = [
  "ARTICLE",
  "GLOSSARY",
  "HANDBOOK",
  "LOCATION",
  "MARKET",
  "PLATFORM",
  "INSURANCE",
  "AIR_QUALITY",
] as const;

export type ResourceSearchFamily = (typeof resourceSearchFamilies)[number];

export const resourceSearchKinds = [
  "ARTICLE",
  "GLOSSARY_TERM",
  "HANDBOOK",
  "HANDBOOK_CHAPTER",
  "LOCATION_PROVINCE",
  "LOCATION_CITY",
  "LOCATION_BOROUGH",
  "MARKET_METRIC",
  "MARKET_METRIC_LOCATION",
  "MARKET_REPORT",
  "MARKET_REPORT_LOCATION",
  "PLATFORM",
  "PLATFORM_COMPARISON",
  "PLATFORM_CITY",
  "PLATFORM_PROVINCE",
  "INSURANCE_PROVIDER",
  "INSURANCE_PRODUCT",
  "AIR_QUALITY_COMPONENT",
  "AIR_QUALITY_STATION",
  "AIR_QUALITY_CITY",
] as const;

export type ResourceSearchKind = (typeof resourceSearchKinds)[number];

export const marketRequestStatusOptions = [
  "PENDING",
  "IN_PROGRESS",
  "COMPLETED",
  "FAILED",
  "CANCELLED",
] as const;
export type MarketRequestStatus = (typeof marketRequestStatusOptions)[number];

export const marketTopicKinds = ["METRIC", "REPORT"] as const;
export type MarketTopicKind = (typeof marketTopicKinds)[number];

export const marketTopicPageTypes = ["LIST", "FULL", "LOCATION"] as const;
export type MarketTopicPageType = (typeof marketTopicPageTypes)[number];

export const marketTopicGroups = [
  "GENERAL",
  "PRICING",
  "SUPPLY",
  "COMPOSITION",
  "SPACE",
  "VALUE",
  "AFFORDABILITY",
  "LIQUIDITY",
  "FURNISHING",
] as const;
export type MarketTopicGroup = (typeof marketTopicGroups)[number];

export const marketTopicAudienceOptions = [
  "RENTER",
  "LANDLORD",
  "INVESTOR",
  "ANALYST",
  "RESEARCHER",
  "JOURNALIST",
  "POLICY",
] as const;
export type MarketTopicAudience = (typeof marketTopicAudienceOptions)[number];

export const marketTopicUnits = [
  "NONE",
  "EUR",
  "EUR_PER_M2",
  "DAYS",
  "COUNT",
  "PERCENT",
  "INDEX",
] as const;
export type MarketTopicUnit = (typeof marketTopicUnits)[number];

export const marketTopicValueFormats = [
  "NONE",
  "CURRENCY",
  "CURRENCY_PER_M2",
  "INTEGER",
  "PERCENT",
  "DAYS",
  "INDEX",
] as const;
export type MarketTopicValueFormat = (typeof marketTopicValueFormats)[number];

export const marketTopicAggregationModes = [
  "MEDIAN",
  "AVERAGE",
  "COUNT",
  "SHARE",
  "DISTRIBUTION",
  "INDEX",
  "MATRIX",
] as const;
export type MarketTopicAggregationMode =
  (typeof marketTopicAggregationModes)[number];

export const marketTopicComparisonModes = [
  "TIME_SERIES",
  "AREA_COMPARISON",
  "SEGMENT_BREAKDOWN",
  "DISTRIBUTION",
  "MATRIX",
] as const;
export type MarketTopicComparisonMode =
  (typeof marketTopicComparisonModes)[number];

export const rentPropertyTypeGroups = [
  "CORE",
  "FURNISHING",
  "BEDROOMS",
  "OUTDOOR_SPACE",
  "TENANT_TYPE",
  "PROPERTY_STATE",
  "SUSTAINABILITY",
  "HOUSEHOLD",
  "LAYOUT",
] as const;

export type RentPropertyTypeGroup = (typeof rentPropertyTypeGroups)[number];

export const rentPropertyTypeAudienceOptions = [
  "youngProfessionals",
  "remoteWorkers",
  "digitalNomads",
  "internationalStudents",
  "graduateStudents",
  "exchangeStudents",
  "singleParents",
  "youngFamilies",
  "largeFamilies",
  "professionalCouples",
  "retirees",
  "emptyNesters",
  "corporateRelocators",
  "shortTermRenters",
  "longTermPlanners",
  "petOwners",
  "ecoConsciousRenters",
  "creativesAndArtists",
  "freelancers",
  "techWorkers",
  "healthcareWorkers",
  "hospitalityWorkers",
  "manualLaborers",
  "startupEmployees",
  "highIncomeExpats",
  "budgetRenters",
  "luxurySeekers",
  "gardenLovers",
  "balconySeekers",
  "cityCenterLovers",
  "quietNeighborhoodSeekers",
  "commuters",
  "carOwners",
  "bikeCommuters",
  "studentsSharing",
  "privacySeekers",
  "firstTimeRenters",
  "frequentTravelers",
  "familiesWithPets",
  "renovationEnthusiasts",
  "greenEnergySupporters",
  "homeOfficeUsers",
  "storageNeeds",
  "artistsNeedingStudios",
  "peopleWithDisabilities",
  "techEnthusiasts",
  "fitnessEnthusiasts",
  "foodiesAndCooks",
  "bookLovers",
] as const;

export type RentPropertyTypeAudience =
  (typeof rentPropertyTypeAudienceOptions)[number];

export const exploreTypeGroups = [
  "GENERAL",
  "AFFORDABILITY",
  "AVAILABILITY",
  "RECENCY",
  "BEDROOMS",
  "PROPERTY_TYPE",
  "STUDENT",
  "VALUE",
] as const;
export type ExploreTypeGroup = (typeof exploreTypeGroups)[number];

export const exploreTypeAudienceOptions = [
  "GENERAL",
  "STUDENT",
  "EXPAT",
  "FAMILY",
  "ROOMMATE",
  "PROFESSIONAL",
] as const;
export type ExploreTypeAudience = (typeof exploreTypeAudienceOptions)[number];

export const userListingSavedFromOptions = [
  "LISTING",
  "MAP",
  "SEARCH",
] as const;

export type UserListingSavedFrom = (typeof userListingSavedFromOptions)[number];

export type SearchAlertSettings = {
  frequency: "IMMEDIATE";
  channel: "EMAIL";
};

export const userBoardColumnOptions = [
  "SAVED",
  "EMAILED",
  "VIEWING_SCHEDULED",
  "APPLIED",
  "REJECTED",
] as const;

export type UserBoardColumn = (typeof userBoardColumnOptions)[number];

export type BoardDetails = {
  VIEWING_SCHEDULED?: {
    startAt?: string; // ISO datetime
    locationText?: string;
  };
  APPLIED?: {
    appliedAt?: string; // ISO datetime
  };
  EMAILED?: {
    emailedAt?: string; // ISO datetime
    email?: string;
  };
};

export const tenantProfileContactOptions = ["EMAIL", "PHONE", "ANY"] as const;

export type TenantProfileContactOption =
  (typeof tenantProfileContactOptions)[number];

export const tenantProfileEmploymentStatusOptions = [
  "EMPLOYED",
  "SELF_EMPLOYED",
  "STUDENT",
  "UNEMPLOYED",
  "RETIRED",
  "OTHER",
] as const;

export type TenantProfileEmploymentStatus =
  (typeof tenantProfileEmploymentStatusOptions)[number];

export const smartMessageTypeOptions = [
  "GENERAL_QUESTION",
  "VIEWING_REQUEST",
  "APPLICATION",
  "FOLLOW_UP",
  "THANK_YOU",
  "OTHER",
] as const;
export type SmartMessageType = (typeof smartMessageTypeOptions)[number];

export type SmartMessageContext = {
  listing?: SmartMessageListingInformation;
};

export type SmartMessageListingInformation = {
  id: string;
  title: string;
  price: number;
  size: number;
  address: string;
  owner: {
    type: OwnerType;
    name: string;
  };
};

export const userDiscoverySourceOptions = [
  "SEARCH",
  "SOCIAL",
  "FRIEND",
  "BLOG",
  "AD",
  "OTHER",
] as const;

export type UserDiscoverySource = (typeof userDiscoverySourceOptions)[number];

export const issueReportStatusOptions = [
  "PENDING",
  "IN_REVIEW",
  "RESOLVED",
  "CLOSED",
] as const;

export type IssueReportStatus = (typeof issueReportStatusOptions)[number];

// Publisher accounts are managed by one user; no external organization is required.
export const dashboardModes = ["RENTER", "PUBLISHER", "DEVELOPER"] as const;
export const publisherTypes = ["INDIVIDUAL", "ORGANIZATION"] as const;
export const publisherStatuses = ["ACTIVE", "SUSPENDED", "CLOSED"] as const;
export const listingPublicationStatuses = [
  "PUBLISHED",
  "PAUSED",
  "RENTED",
  "ARCHIVED",
  "SUSPENDED",
] as const;
export const listingDraftStatuses = [
  "DRAFT",
  "IN_REVIEW",
  "CHANGES_REQUESTED",
] as const;

// Draft fields are optional until submission. Validate content in the publishing
// flow before copying it into the required public listing columns.
export type ListingDraftContent = {
  sourceLanguage?: Locale;
  textRevision?: string;
  translatedTextRevision?: string;
  publishedText?: {
    language: Locale;
    title: string;
    description: string;
    revision: string;
    translated: boolean;
  };
  label?: string;
  type?: ListingType;
  size?: number;
  price?: number;
  energyLabel?: string | null;
  images?: string[];
  roomsCount?: number;
  furnishingType?: FurnishingType;
  attributes?: Partial<ListingAttributes>;
  location?: {
    country?: string;
    neighborhood?: string | null;
    city?: string;
    province?: string | null;
    borough?: string | null;
    street?: string;
    number?: string | null;
    postalCode?: string;
    point?: { x: number; y: number };
  };
  texts?: Partial<
    Record<
      Locale,
      {
        title?: string;
        headline?: string;
        description?: string;
      }
    >
  >;
};

export const listingPromotionPaymentStatuses = [
  "PENDING",
  "PAID",
  "FAILED",
  "CANCELED",
  "REFUNDED",
] as const;
